/**
 * Dynamic Configuration System
 * Main configuration file that loads and manages configurations
 */
import ConfigManager from '../utils/ConfigManager';
import { 
  environmentLoader, 
  createExternalJsonLoader, 
  localStorageLoader, 
  remoteApiLoader,
  windowGlobalLoader 
} from './loaders';

// Create global configuration manager instance
const configManager = new ConfigManager();

// Add configuration sources in priority order (last added has highest priority)
configManager.addSource('environment-files', environmentLoader);
// Use the improved external JSON loader with dynamic base path
configManager.addSource('external-json', createExternalJsonLoader(configManager.getBasePath()));
configManager.addSource('remote-api', remoteApiLoader);
configManager.addSource('local-storage', localStorageLoader);
configManager.addSource('window-global', windowGlobalLoader);

// Initialize configuration
let configPromise = null;
let isConfigLoaded = false;

/**
 * Initialize and load configuration
 */
export const initializeConfig = async () => {
  if (configPromise) {
    return configPromise;
  }

  configPromise = configManager.loadConfig();
  
  try {
    const config = await configPromise;
    isConfigLoaded = true;
    
    // Set global config for backward compatibility
    if (typeof global !== 'undefined') {
      global.config = config;
    }
    if (typeof window !== 'undefined') {
      window.APP_CONFIG = config;
    }
    
    return config;
  } catch (error) {
    console.error('Failed to initialize configuration:', error);
    isConfigLoaded = false;
    throw error;
  }
};

/**
 * Get configuration (async)
 */
export const getConfig = async () => {
  if (!isConfigLoaded) {
    return await initializeConfig();
  }
  return configManager.getConfig();
};

/**
 * Get configuration synchronously (returns null if not loaded)
 */
export const getConfigSync = () => {
  if (!isConfigLoaded) {
    console.warn('Configuration not loaded yet. Use getConfig() or await initializeConfig()');
    return null;
  }
  return configManager.getConfig();
};

/**
 * Get specific configuration value
 */
export const getConfigValue = async (path, defaultValue = undefined) => {
  const config = await getConfig();
  return path.split('.').reduce((obj, key) => obj?.[key], config) ?? defaultValue;
};

/**
 * Get specific configuration value synchronously
 */
export const getConfigValueSync = (path, defaultValue = undefined) => {
  const config = getConfigSync();
  if (!config) return defaultValue;
  return path.split('.').reduce((obj, key) => obj?.[key], config) ?? defaultValue;
};

/**
 * Set configuration value
 */
export const setConfigValue = (path, value) => {
  configManager.set(path, value);
};

/**
 * Get current environment
 */
export const getEnvironment = () => {
  return configManager.getEnvironment();
};

/**
 * Check if running in production
 */
export const isProduction = () => {
  return configManager.isProduction();
};

/**
 * Check if running in development
 */
export const isDevelopment = () => {
  return configManager.isDevelopment();
};

/**
 * Check if system is in maintenance mode
 */
export const isMaintenance = () => {
  try {
    
    const config = getConfigSync();
    
    // In development, check for an override flag in localStorage
    if (config?.deployment?.environment === 'development' && 
        localStorage.getItem('maintenance_disabled') === 'true') {
      console.log('Maintenance mode disabled via localStorage override');
      return false;
    }
    
    // Check multiple sources for maintenance flag
    const maintenanceFlag = 
                           window.RUNTIME_CONFIG?.maintenance?.enabled ||
                           window.APP_CONFIG?.maintenance?.enabled ||
                           localStorage.getItem('maintenance_mode') === 'true';
    
    // console.log('Maintenance check results:', {
    //   configEnabled: config?.maintenance?.enabled,
    //   runtimeConfigEnabled: window.RUNTIME_CONFIG?.maintenance?.enabled,
    //   appConfigEnabled: window.APP_CONFIG?.maintenance?.enabled,
    //   localStorageEnabled: localStorage.getItem('maintenance_mode') === 'true',
    //   finalResult: Boolean(maintenanceFlag),
    //   environment: config?.deployment?.environment
    // });
    
    return Boolean(maintenanceFlag);
  } catch (error) {
    console.error('Error checking maintenance mode:', error);
    return false;
  }
};

/**
 * Get maintenance configuration
 */
export const getMaintenanceConfig = () => {
  try {
    const config = getConfigSync();
    
    // Get endTime from various sources
    const endTimeString = config?.maintenance?.endTime ||
                         window.RUNTIME_CONFIG?.maintenance?.endTime ||
                         window.APP_CONFIG?.maintenance?.endTime ||
                         null;
    
    // Parse endTime from "yyyy-MM-dd HH:mm:ss" UTC format
    let parsedEndTime = null;
    if (endTimeString) {
      try {
        // Check if it's already in ISO format
        if (endTimeString.includes('T') || endTimeString.includes('Z')) {
          parsedEndTime = new Date(endTimeString);
        } else {
          // Parse "yyyy-MM-dd HH:mm:ss" format as UTC
          // Add 'Z' to indicate UTC time
          const isoString = endTimeString.replace(' ', 'T') + 'Z';
          parsedEndTime = new Date(isoString);
        }
        
        // Validate the parsed date
        if (isNaN(parsedEndTime.getTime())) {
          console.warn('Invalid maintenance endTime format:', endTimeString);
          parsedEndTime = null;
        }
      } catch (error) {
        console.error('Error parsing maintenance endTime:', error);
        parsedEndTime = null;
      }
    }
    
    return {
      enabled: isMaintenance(),
      message: config?.maintenance?.message || 
               window.RUNTIME_CONFIG?.maintenance?.message ||
               window.APP_CONFIG?.maintenance?.message ||
               'System is currently under maintenance.',
      endTime: parsedEndTime
    };
  } catch (error) {
    console.error('Error getting maintenance config:', error);
    return {
      enabled: false,
      message: 'System is currently under maintenance.',
      endTime: null
    };
  }
};

/**
 * Check if running in staging
 */
export const isStaging = () => {
  return configManager.isStaging();
};

/**
 * Reload configuration
 */
export const reloadConfig = async () => {
  configPromise = null;
  isConfigLoaded = false;
  return await initializeConfig();
};

/**
 * Get configuration manager instance (for advanced usage)
 */
export const getConfigManager = () => {
  return configManager;
};

// Export default configuration for backward compatibility
const ConfigAPI = {
  init: initializeConfig,
  get: getConfig,
  getSync: getConfigSync,
  getValue: getConfigValue,
  getValueSync: getConfigValueSync,
  setValue: setConfigValue,
  getEnvironment,
  isProduction,
  isDevelopment,
  isStaging,
  isMaintenance,
  getMaintenanceConfig,
  reload: reloadConfig,
  manager: configManager
};

export default ConfigAPI;
